import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useEnrollment } from '../../contexts/EnrollmentContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCourse } from '../../contexts/CourseContext';
import {
  BookOpen,
  Play,
  Clock,
  Users,
  Star,
  CheckCircle,
  ArrowRight,
  Loader
} from 'lucide-react';

const CourseEnrollment = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { courses } = useCourse();
  const { enrollInCourse, isEnrolled } = useEnrollment();

  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [course, setCourse] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(true);

  useEffect(() => {
    const fromContext = courses.find(c => String(c.id) === String(courseId));
    if (fromContext) { setCourse(fromContext); setLoadingCourse(false); return; }
    // Cours non encore dans le contexte (ex: brouillon visible par instructeur)
    axios.get(`/api/courses/${courseId}`)
      .then(res => {
        const c = res.data.course || null;
        if (c) {
          const raw = c.instructor;
          if (raw && typeof raw === 'object') {
            c.instructor = `${raw.first_name || ''} ${raw.last_name || ''}`.trim() || 'Instructeur';
          }
        }
        setCourse(c);
      })
      .catch(() => setCourse(null))
      .finally(() => setLoadingCourse(false));
  }, [courseId, courses]);

  const enrolled = isEnrolled(courseId);

  const handleEnroll = async () => {
    if (!user || !course) return;

    setIsEnrolling(true);
    try {
      const result = await enrollInCourse(courseId);
      if (result.success) {
        setEnrollmentSuccess(true);
        setTimeout(() => { navigate(`/course/${courseId}/learn`); }, 2000);
      } else {
        toast.error(result.error || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleContinueLearning = () => {
    navigate(`/course/${courseId}/learn`);
  };

  if (loadingCourse) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Cours non trouvé
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Le cours demandé n'existe pas ou a été supprimé.
          </p>
        </div>
      </div>
    );
  }

  if (enrollmentSuccess) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            Inscription réussie !
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Vous êtes maintenant inscrit au cours "{course.title}". 
            Vous allez être redirigé vers le contenu du cours...
          </p>
          <div className="flex items-center justify-center gap-2 text-primary">
            <Loader className="w-4 h-4 animate-spin" />
            <span>Redirection en cours...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">

        {/* Hero — deux colonnes : texte à gauche, vignette à droite */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800">
          <div className="flex flex-col md:flex-row gap-6 p-6 md:p-8 items-start">
            {/* Infos textuelles */}
            <div className="flex-1 text-white min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold mb-3 leading-tight">{course.title}</h1>
              {course.description && (
                <p className="text-gray-300 text-sm mb-4 line-clamp-3">{course.description}</p>
              )}
              <div className="flex flex-wrap gap-4 text-sm text-gray-300">
                {course.instructor && (
                  <span className="flex items-center gap-1.5">
                    <BookOpen className="w-4 h-4" /> {course.instructor}
                  </span>
                )}
                {(course.average_rating > 0 || course.rating > 0) && (
                  <span className="flex items-center gap-1.5">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    {course.average_rating || course.rating}
                  </span>
                )}
                {(course.enrollments_count || course.students) > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Users className="w-4 h-4" />
                    {course.enrollments_count || course.students} étudiants
                  </span>
                )}
                {(course.estimated_duration || course.duration) && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {course.estimated_duration || course.duration}
                  </span>
                )}
              </div>
            </div>

            {/* Vignette contenue, sans distorsion */}
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="aspect-video bg-gray-700 rounded-lg overflow-hidden flex items-center justify-center">
                {course.thumbnail ? (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <BookOpen className="w-14 h-14 text-gray-500" />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Informations du cours */}
          <div className={`grid grid-cols-1 ${course.whatYouWillLearn?.length ? 'md:grid-cols-2' : ''} gap-6 mb-6`}>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                À propos de ce cours
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {course.description}
              </p>
            </div>

            {course.whatYouWillLearn?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Ce que vous apprendrez
                </h3>
                <ul className="space-y-2">
                  {course.whatYouWillLearn.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Prérequis */}
          {course.requirements && course.requirements.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Prérequis
              </h3>
              <ul className="space-y-1">
                {course.requirements.map((requirement, index) => (
                  <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                    • {requirement}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            {enrolled ? (
              <div className="text-center">
                <div className="mb-4">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    Vous êtes inscrit à ce cours
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Continuez votre apprentissage où vous vous êtes arrêté
                  </p>
                </div>
                <button
                  onClick={handleContinueLearning}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mx-auto"
                >
                  <Play className="w-4 h-4" />
                  Continuer l'apprentissage
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    Prêt à commencer ?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Rejoignez {course.students} étudiants dans ce cours
                  </p>
                </div>
                <button
                  onClick={handleEnroll}
                  disabled={isEnrolling}
                  className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mx-auto"
                >
                  {isEnrolling ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Inscription en cours...
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-4 h-4" />
                      S'inscrire au cours
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseEnrollment;
